import { useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { X, User as UserIcon, FileText, ArrowRight, ExternalLink, Code, Sparkles } from 'lucide-react';
import { formatIst } from '../../lib/time';
import { MODULE_BADGE, actionBadge, diffChangedFields, redactSensitive, humanLabel, entityHref } from './activityConstants';
import { LogValue } from './logValueView';

// Housekeeping columns that are just noise in an audit view — never surface them as a "change" or a
// snapshot field (matched case/underscore-insensitively so createdAt & created_at both drop out).
const NOISE_KEYS = new Set(['id', 'uuid', 'createdat', 'updatedat', 'deletedat', 'createdby', 'updatedby', 'version']);
function stripNoise(v: any): any {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return v;
  const out: Record<string, unknown> = {};
  for (const [k, val] of Object.entries(v)) {
    if (!NOISE_KEYS.has(k.toLowerCase().replace(/_/g, ''))) out[k] = val;
  }
  return out;
}

export interface ActorRef { id: string; name: string; role: string; phone?: string | null }
export interface ActivityRow {
  id: string;
  module: string;
  action: string;
  referenceId: string | null;
  referenceLabel?: string | null;
  oldValue: any;
  newValue: any;
  message: string | null;
  performedBy: string | null;
  actingOnBehalfOf: string | null;
  source: string | null;
  ipAddress: string | null;
  createdAt: string;
  actor: ActorRef | null;
  onBehalfOf: ActorRef | null;
}

// Parse `<sentence> (field_a, field_b)` → { prefix, fields[] } so the trailing
// changed-field list renders as readable chips instead of snake_case noise.
function parseTrailingFieldList(message?: string | null): { prefix: string; fields: string[] } {
  if (!message) return { prefix: '', fields: [] };
  const m = message.match(/^(.*?)\s*\(([\w-]+(?:\s*,\s*[\w-]+)+)\)\s*$/);
  if (!m) return { prefix: message, fields: [] };
  const fields = m[2].split(',').map((s) => s.trim()).filter(Boolean);
  if (fields.length < 2) return { prefix: message, fields: [] };
  return { prefix: m[1].trim(), fields };
}

function softenEnum(s: unknown): string {
  if (typeof s !== 'string') return String(s);
  if (!/^[A-Z][A-Z0-9_]+$/.test(s)) return s;
  return s.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}
function formatScalar(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  const s = String(v).trim();
  if (/^-?\d+\.\d+$/.test(s)) { const n = Number(s); if (Number.isFinite(n)) return n.toString(); }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(s)) return formatIst(s);
  return s;
}

function ObjectCard({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data || {}).filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (!entries.length) return <span className="text-muted italic">—</span>;
  return (
    <div className="space-y-1">
      {entries.map(([k, v]) => (
        <div key={k} className="flex gap-2 text-2xs leading-tight">
          <span className="min-w-[80px] text-muted">{humanLabel(k)}</span>
          <span className="break-words text-content">
            {v && typeof v === 'object' ? <ObjectCard data={v as Record<string, unknown>} /> : softenEnum(formatScalar(v))}
          </span>
        </div>
      ))}
    </div>
  );
}

function renderCell(value: unknown): ReactNode {
  if (value === undefined || value === null || value === '') return <span className="text-muted italic">—</span>;
  if (typeof value === 'boolean') return value ? <span className="font-medium text-emerald-600">Yes</span> : <span className="font-medium text-rose-600">No</span>;
  if (Array.isArray(value)) {
    if (!value.length) return <span className="text-muted italic">empty</span>;
    if (value.every((v) => v && typeof v === 'object')) {
      return <div className="space-y-1.5">{value.map((it, i) => <div key={i} className="rounded-[8px] border border-line bg-surface px-2 py-1.5"><ObjectCard data={it as Record<string, unknown>} /></div>)}</div>;
    }
    return <div className="flex flex-wrap gap-1">{value.map((v, i) => <span key={i} className="inline-block rounded bg-surface-2 px-1.5 py-0.5 text-2xs text-content">{softenEnum(formatScalar(v))}</span>)}</div>;
  }
  if (typeof value === 'object') return <ObjectCard data={value as Record<string, unknown>} />;
  return <span className="break-words text-content">{formatScalar(value)}</span>;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-2xs uppercase tracking-widest text-muted">{label}</div>
      <div className="mt-0.5 text-sm text-content">{children}</div>
    </div>
  );
}

export function ActivityLogDetailDrawer({ log, onClose }: { log: ActivityRow; onClose: () => void }) {
  const [showRaw, setShowRaw] = useState(false);
  const oldRaw = redactSensitive(log.oldValue);
  const newRaw = redactSensitive(log.newValue);
  // The diff + snapshot show only MEANINGFUL fields (housekeeping columns stripped); the "full snapshot"
  // below keeps everything for power users.
  const oldData = stripNoise(oldRaw);
  const newData = stripNoise(newRaw);
  const changed = diffChangedFields(oldData, newData);
  const oldObj = oldData && typeof oldData === 'object' && !Array.isArray(oldData) ? (oldData as Record<string, unknown>) : null;
  const newObj = newData && typeof newData === 'object' && !Array.isArray(newData) ? (newData as Record<string, unknown>) : null;
  const nonEmpty = (o: Record<string, unknown> | null) => !!o && Object.values(o).some((v) => v !== null && v !== undefined && v !== '');
  const hasOld = nonEmpty(oldObj);
  const hasNew = nonEmpty(newObj);
  // A true before→after only when BOTH sides exist; otherwise render a labeled one-sided snapshot so a
  // create / delete / import job doesn't show confusing "— → x" or "x → —" rows.
  const diffMode = hasOld && hasNew && changed.length > 0;
  const isDelete = /DELETE|REMOV/i.test(log.action);
  const snapshotObj = diffMode ? null : hasNew ? newObj : hasOld ? oldObj : null;
  const snapshotEntries = Object.entries(snapshotObj || {}).filter(([, v]) => v !== null && v !== undefined && v !== '');
  const snapshotTitle = isDelete ? 'Removed record'
    : /IMPORT/i.test(log.action) ? 'Import summary'
    : /CREATE|REGISTER|ADD/i.test(log.action) ? 'Created'
    : 'Details';
  const href = entityHref(log.module, log.referenceId);
  const actor = log.actor;
  const { prefix, fields } = parseTrailingFieldList(log.message);

  return createPortal(
    <>
      <div className="fixed inset-0 z-[1000] bg-black/30" onClick={onClose} aria-hidden />
      <aside className="fixed bottom-0 right-0 top-0 z-[1001] flex w-full flex-col border-l border-line bg-surface shadow-2xl sm:w-[480px] lg:w-[560px]" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between border-b border-line bg-surface px-5 py-4">
          <div className="min-w-0">
            <div className="text-2xs uppercase tracking-widest text-muted">Activity log #{log.id}</div>
            <h2 className="mt-0.5 truncate text-base font-semibold text-content">{log.message || `${log.module} · ${log.action}`}</h2>
          </div>
          <button onClick={onClose} className="-m-1.5 rounded p-1.5 text-muted hover:bg-surface-2" aria-label="Close"><X className="size-[18px]" /></button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
          {/* 1. Summary */}
          <section>
            <h3 className="mb-2 text-2xs uppercase tracking-widest text-muted">Summary</h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Module"><span className={`inline-block rounded px-2 py-0.5 text-2xs font-medium ${MODULE_BADGE[log.module] ?? 'bg-surface-2 text-content'}`}>{log.module}</span></Field>
              <Field label="Action"><span className={`inline-block rounded px-2 py-0.5 text-2xs font-medium ${actionBadge(log.action)}`}>{log.action.replace(/_/g, ' ')}</span></Field>
              <div className="col-span-2"><Field label="When">{formatIst(log.createdAt)}</Field></div>
              {log.message && (
                <div className="col-span-2">
                  <div className="mb-1 text-2xs uppercase tracking-widest text-muted">Message</div>
                  <div className="rounded-[10px] border border-line bg-surface-2/60 px-3 py-2.5">
                    <div className="text-sm leading-relaxed text-content break-words">{prefix}</div>
                    {fields.length > 0 && (
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <span className="mr-0.5 text-2xs uppercase tracking-widest text-muted">Fields:</span>
                        {fields.map((f) => <span key={f} className="inline-block rounded bg-amber-100 px-2 py-0.5 text-2xs font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">{humanLabel(f)}</span>)}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* 2. Actor */}
          <section>
            <h3 className="mb-2 inline-flex items-center gap-1.5 text-2xs uppercase tracking-widest text-muted"><UserIcon className="size-3" /> Actor</h3>
            {actor ? (
              <div className="grid grid-cols-2 gap-3 rounded-[10px] border border-line p-3">
                <Field label="Name">{actor.name || '—'}</Field>
                <Field label="Role">{actor.role || '—'}</Field>
                <Field label="Phone">{actor.phone || '—'}</Field>
                {log.onBehalfOf && (
                  <div className="col-span-2"><Field label="On behalf of">{log.onBehalfOf.name} ({log.onBehalfOf.role})</Field></div>
                )}
              </div>
            ) : (
              <div className="text-sm italic text-muted">{log.source || 'System'} (no actor recorded)</div>
            )}
          </section>

          {/* 3. Entity */}
          <section>
            <h3 className="mb-2 inline-flex items-center gap-1.5 text-2xs uppercase tracking-widest text-muted"><FileText className="size-3" /> Entity</h3>
            <div className="grid grid-cols-2 gap-3 rounded-[10px] border border-line p-3">
              <Field label="Module">{log.module}</Field>
              <Field label="Reference">{log.referenceLabel || '—'}</Field>
              {href && (
                <div className="col-span-2">
                  <Link to={href} onClick={onClose} className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:underline dark:text-brand-300">
                    Open record <ExternalLink className="size-3" />
                  </Link>
                </div>
              )}
            </div>
          </section>

          {/* 4a. Genuine edit → a real before → after diff. */}
          {diffMode && (
            <section>
              <div className="rounded-[14px] border border-amber-200 bg-amber-50/40 p-3 dark:border-amber-900/40 dark:bg-amber-900/10">
                <h3 className="mb-2.5 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-amber-700 dark:text-amber-300">
                  <Sparkles className="size-3.5 text-amber-500" /> What changed ({changed.length})
                </h3>
                <div className="overflow-hidden rounded-[10px] border border-amber-100 bg-surface dark:border-amber-900/30">
                  <table className="w-full text-xs">
                    <thead className="bg-amber-50/60 text-2xs uppercase tracking-widest text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                      <tr>
                        <th className="px-3 py-2 text-left font-bold">Field</th>
                        <th className="px-3 py-2 text-left font-bold">Before</th>
                        <th className="w-6 px-1 py-2" />
                        <th className="px-3 py-2 text-left font-bold">After</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line">
                      {changed.map((c) => (
                        <tr key={c.field} className="align-top">
                          <td className="whitespace-nowrap px-3 py-2 font-semibold text-content">{humanLabel(c.field)}</td>
                          <td className="max-w-[180px] px-3 py-2">{renderCell(c.old)}</td>
                          <td className="px-1 py-2 text-amber-400"><ArrowRight className="size-3.5" /></td>
                          <td className="max-w-[180px] px-2 py-2">
                            <div className="rounded-md bg-amber-50/50 px-2 py-1.5 ring-2 ring-amber-300 dark:bg-amber-900/20 dark:ring-amber-700">{renderCell(c.new)}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          )}

          {/* 4b. Create / delete / import (one-sided) → a labeled snapshot, not a misleading diff. */}
          {!diffMode && snapshotEntries.length > 0 && (
            <section>
              <div className="rounded-[14px] border border-line bg-surface-2/30 p-3">
                <h3 className="mb-2.5 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-muted">
                  <FileText className="size-3.5" /> {snapshotTitle} ({snapshotEntries.length})
                </h3>
                <div className="overflow-hidden rounded-[10px] border border-line bg-surface">
                  <table className="w-full text-xs">
                    <tbody className="divide-y divide-line">
                      {snapshotEntries.map(([k, v]) => (
                        <tr key={k} className="align-top">
                          <td className="w-[42%] whitespace-nowrap px-3 py-2 font-semibold text-content">{humanLabel(k)}</td>
                          <td className={`px-3 py-2 ${isDelete ? 'text-muted' : ''}`}>{renderCell(v)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          )}

          {/* 5. Technical view — the COMPLETE payload (housekeeping fields included). */}
          {(oldRaw || newRaw) && (
            <section>
              <button type="button" onClick={() => setShowRaw((v) => !v)} className="inline-flex items-center gap-1.5 text-2xs text-muted hover:text-content">
                <Code className="size-3" /> {showRaw ? 'Hide full snapshot' : 'Show full snapshot'}
              </button>
              {showRaw && (
                <div className="mt-2 space-y-3">
                  {oldRaw != null && (
                    <div className="rounded-[10px] border border-line">
                      <div className="px-3 py-2 text-2xs uppercase tracking-widest text-muted">Before</div>
                      <div className="border-t border-line bg-surface-2/40 px-3 py-2"><LogValue value={oldRaw} /></div>
                    </div>
                  )}
                  {newRaw != null && (
                    <div className="rounded-[10px] border border-line">
                      <div className="px-3 py-2 text-2xs uppercase tracking-widest text-muted">After</div>
                      <div className="border-t border-line bg-surface-2/40 px-3 py-2"><LogValue value={newRaw} /></div>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}
        </div>
      </aside>
    </>,
    document.body,
  );
}
